import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Heart, Loader2 } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { useInAppPurchase } from '@/hooks/useInAppPurchase'
import { useScrollLock } from '@/hooks/useScrollLock'
import { markAsDonated } from '@/hooks/useEngagement'
import { Portal } from '@/components/Portal'
import { StreakIcon } from './icons/StreakIcons'

// Sur iOS natif → IAP Apple exclusivement. Sur Android / Web → Stripe.
const IS_IOS = Capacitor.getPlatform() === 'ios'

// URL du lien Stripe Payment — utilisée UNIQUEMENT sur Android et Web.
// N'est jamais référencée ni chargée sur iOS.
const STRIPE_URL =
  import.meta.env.VITE_STRIPE_DONATION_URL ||
  'https://buy.stripe.com/bJedR85iO9bOaPq4EOcwg00'

interface Props { days: 7 | 30; onClose: () => void }

export function DonationPopup({ days, onClose }: Props) {
  const is30 = days === 30
  useScrollLock()

  // IAP — utilisé sur iOS uniquement ; sur web les appels natifs échouent silencieusement
  const { purchase, purchasing, purchaseSuccess } = useInAppPurchase()

  // Fermer le popup dès qu'un achat IAP est confirmé + marquer comme donateur
  useEffect(() => {
    if (purchaseSuccess) {
      markAsDonated()
      onClose()
    }
  }, [purchaseSuccess, onClose])

  // Couleur bouton : noir en light, blanc en dark (via variables CSS)
  const btnBg   = 'var(--selah-button-bg)'
  const btnText = 'var(--selah-button-text)'

  return (
    <Portal>
    <AnimatePresence>
      <motion.div
        key="donation-backdrop"
        data-modal="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 flex items-end justify-center p-4"
        style={{
          backgroundColor: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 9998,
        }}
        onClick={onClose}
      >
        <motion.div
          key="donation-card"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 260 }}
          className="relative w-full max-w-sm p-6 shadow-2xl"
          style={{
            backgroundColor: 'var(--selah-card)',
            border: '1px solid var(--selah-border)',
            borderRadius: '20px',
            maxHeight: 'calc(100dvh - env(safe-area-inset-top, 44px) - 80px)',
            overflowY: 'auto',
            paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 34px))',
            zIndex: 9999,
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-full"
            style={{ color: 'var(--selah-text-secondary)' }}
          >
            <X size={20} />
          </button>

          {/* Contenu */}
          <div className="text-center space-y-4">
            <div style={{ color: 'var(--selah-text)' }}><StreakIcon streak={is30 ? 30 : 7} size={48} /></div>

            <div>
              <h2 className="text-xl font-bold" style={{ color: 'var(--selah-text)' }}>
                {is30 ? '30 jours avec Selah !' : '7 jours de fidélité !'}
              </h2>
              <p className="mt-1" style={{ color: 'var(--selah-text-secondary)', fontSize: 15, lineHeight: 1.5 }}>
                {is30
                  ? 'Un mois de croissance spirituelle. Si Selah vous a été utile, pensez à nous soutenir pour que le projet continue.'
                  : 'Une semaine déjà ! Si cette app enrichit votre vie de foi, un petit geste peut nous aider à continuer.'}
              </p>
            </div>

            {IS_IOS ? (
              /* ── iOS natif : bouton IAP — aucune référence à Stripe ── */
              <button
                type="button"
                onClick={() => purchase('selah_donation_cinq')}
                disabled={purchasing}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl font-semibold transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: btnBg,
                  color: btnText,
                  opacity: purchasing ? 0.6 : 1,
                }}
              >
                {purchasing
                  ? <Loader2 size={16} className="animate-spin" />
                  : <Heart size={16} />}
                {purchasing ? 'Traitement…' : 'Soutenir Selah'}
              </button>
            ) : (
              /* ── Web / Android : lien Stripe ── */
              <a
                href={STRIPE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { markAsDonated(); onClose() }}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl font-semibold transition-opacity hover:opacity-90"
                style={{ backgroundColor: btnBg, color: btnText }}
              >
                <Heart size={16} />
                Soutenir Selah
              </a>
            )}

            <button
              onClick={onClose}
              className="w-full py-3 text-sm"
              style={{ color: 'var(--selah-text-secondary)', minHeight: 44 }}
            >
              Plus tard
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
    </Portal>
  )
}
